import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, GENERIC_FIELDS, fieldClass } from '../discovery/fields';

const button =
  'inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium transition disabled:opacity-50';
const primaryButton = `${button} bg-primary-600 text-white hover:bg-primary-700`;
const quietButton = `${button} text-gray-600 hover:bg-gray-100`;

function shownDefault(settings, fallbackMasterUrl, key) {
  if (key === 'masterUrl' && !settings.masterUrl) return fallbackMasterUrl || '';
  return settings[key] || '';
}

const EnrollmentFileModal = ({ sessionId, onClose, onCreated }) => {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [showKeys, setShowKeys] = useState(false);
  const [settings, setSettings] = useState(null);
  const [fallbackMasterUrl, setFallbackMasterUrl] = useState('');
  const [overrides, setOverrides] = useState({});
  const [role, setRole] = useState('');
  const [machineName, setMachineName] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/enrollment`)
      .then((response) => {
        if (cancelled) return;
        const data = response.data.data || {};
        setSettings(data.settings || {});
        setFallbackMasterUrl(data.fallbackMasterUrl || '');
        setOverrides(data.profile?.overrides || {});
        setRole(data.profile?.role || '');
        setMachineName(data.profile?.machineName || '');
        setEndDate(data.specific?.endDate || '');
        setExclusions((data.specific?.exclusions || []).join(', '));
        setSessionToken(data.specific?.sessionToken || '');
        setCompanyName(data.specific?.companyName || '');
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.response?.data?.error || 'The enrollment values could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pending]);

  const setCustom = (key, custom) => {
    setOverrides((current) => {
      const next = { ...current };
      if (custom) next[key] = shownDefault(settings || {}, fallbackMasterUrl, key);
      else delete next[key];
      return next;
    });
  };

  const download = async (event) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/discovery/sessions/${sessionId}/enrollment`,
        {
          role,
          machineName,
          endDate,
          exclusions: exclusions.split(',').map((item) => item.trim()).filter(Boolean),
          overrides,
        },
        { responseType: 'blob' },
      );
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      if (!blob.size) throw new Error('The enrollment file was empty.');
      const slug = (companyName || 'session').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = `${slug || 'session'}-enrollment.json`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success('Enrollment file downloaded');
      await onCreated();
      onClose();
    } catch (downloadError) {
      let message = downloadError.response?.data?.error || downloadError.message;
      const data = downloadError.response?.data;
      if (data instanceof Blob) {
        const text = await data.text().catch(() => '');
        try {
          message = JSON.parse(text).error || message;
        } catch {
          message = text || message;
        }
      }
      setError(message || 'The enrollment file could not be created.');
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-10">
      <div
        className="w-full max-w-2xl rounded-xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="enrollment-title"
      >
        <form onSubmit={download}>
          <div className="border-b border-gray-200 px-5 py-4">
            <h2 id="enrollment-title" className="text-lg font-semibold text-gray-900">
              Enrollment file
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              These values are written into the JSON for one computer. A custom value replaces the Discovery setting for this file only.
            </p>
          </div>
          {loading ? (
            <p className="px-5 py-8 text-sm text-gray-500">Loading enrollment values…</p>
          ) : (
            <div className="max-h-[70vh] space-y-6 overflow-y-auto px-5 py-4">
              {error ? <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p> : null}
              <section className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">This computer</h3>
                <div>
                  <label htmlFor="enrollment-role" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <input
                    id="enrollment-role"
                    className={fieldClass}
                    value={role}
                    placeholder="order desk"
                    required
                    onChange={(event) => setRole(event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="enrollment-machine" className="block text-sm font-medium text-gray-700">
                    Computer name
                  </label>
                  <input
                    id="enrollment-machine"
                    className={fieldClass}
                    value={machineName}
                    placeholder="Leave blank to use the computer's own name"
                    onChange={(event) => setMachineName(event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="enrollment-end" className="block text-sm font-medium text-gray-700">
                    Engagement end date
                  </label>
                  <input
                    id="enrollment-end"
                    type="date"
                    className={fieldClass}
                    value={endDate}
                    required
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="enrollment-exclusions" className="block text-sm font-medium text-gray-700">
                    Excluded applications
                  </label>
                  <input
                    id="enrollment-exclusions"
                    className={fieldClass}
                    value={exclusions}
                    placeholder="WhatsApp, Spotify"
                    onChange={(event) => setExclusions(event.target.value)}
                  />
                  <p className="mt-1 text-sm text-gray-500">Comma-separated. Saved on the session as well as in the file.</p>
                </div>
                <div>
                  <label htmlFor="enrollment-token" className="block text-sm font-medium text-gray-700">
                    Session token
                  </label>
                  <input id="enrollment-token" className={`${fieldClass} font-mono`} value={sessionToken} readOnly />
                </div>
              </section>
              <section className="space-y-4">
                <div className="flex items-end justify-between gap-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Discovery settings</h3>
                  <Link to="/discovery/settings" className="text-sm font-medium text-primary-700 hover:text-primary-800">
                    Edit defaults
                  </Link>
                </div>
                {GENERIC_FIELDS.map((field) => {
                  const custom = Object.prototype.hasOwnProperty.call(overrides, field.key);
                  const value = custom ? overrides[field.key] : shownDefault(settings || {}, fallbackMasterUrl, field.key);
                  return (
                    <div key={field.key}>
                      <div className="flex items-center justify-between gap-3">
                        <label htmlFor={`enrollment-${field.key}`} className="block text-sm font-medium text-gray-700">
                          {field.label}
                        </label>
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={custom}
                            onChange={(event) => setCustom(field.key, event.target.checked)}
                          />
                          Custom for this file
                        </label>
                      </div>
                      <input
                        id={`enrollment-${field.key}`}
                        className={fieldClass}
                        type={field.secret && !showKeys ? 'password' : 'text'}
                        autoComplete="off"
                        disabled={!custom}
                        value={value}
                        placeholder={field.placeholder}
                        onChange={(event) =>
                          setOverrides((current) => ({ ...current, [field.key]: event.target.value }))
                        }
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        {custom
                          ? 'This file uses this value instead of the Discovery setting.'
                          : field.key === 'masterUrl' && !(settings || {}).masterUrl
                            ? 'No master URL is saved. This file will use the discovery master.'
                            : field.hint}
                      </p>
                    </div>
                  );
                })}
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" checked={showKeys} onChange={(event) => setShowKeys(event.target.checked)} />
                  Show keys
                </label>
              </section>
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button type="button" className={quietButton} onClick={onClose} disabled={pending}>
              Cancel
            </button>
            <button type="submit" className={primaryButton} disabled={pending || loading || !settings}>
              {pending ? 'Preparing…' : 'Download enrollment file'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnrollmentFileModal;
